/**
 * Normalizes a number to prevent precision flickering issues
 * For extremely large numbers, uses scientific notation with consistent precision
 *
 * This function addresses floating point precision issues that occur when:
 * - Same numbers are processed through different calculation paths
 * - Very large numbers (>1e100) lose precision in standard floating point operations
 * - Numbers are serialized/deserialized from GraphQL responses
 *
 * @param value - The number to normalize
 * @param precision - Number of decimal places for normal numbers (default: 6)
 * @returns Normalized number with consistent precision
 */
export function normalizeNumber(value: number, precision: number = 6): number {
  const safeValue = safeNumber(value);

  // For extremely large numbers (> 1e100), use lower precision to avoid flickering
  if (
    Math.abs(safeValue) > 1e100 &&
    Math.abs(safeValue) < Number.MAX_VALUE &&
    Math.abs(safeValue) > Number.MIN_VALUE
  ) {
    // Use scientific notation with reduced precision for very large numbers
    const scientificPrecision = Math.max(2, precision - 2);
    return parseFloat(safeValue.toExponential(scientificPrecision));
  }

  // For normal numbers and numbers that are not finite (max or min value), use standard precision
  return parseFloat(safeValue.toFixed(precision));
}

/**
 * Ensures a number is within safe JavaScript number bounds
 */
export function safeNumber(value: number): number {
  if (Number.isFinite(value)) {
    return value;
  }

  if (value < Number.MIN_VALUE) {
    return Number.MIN_VALUE;
  }
  if (value > Number.MAX_VALUE) {
    return Number.MAX_VALUE;
  }
  return 0;
}

/**
 * Formats a number for display in the UI with appropriate precision
 * Uses scientific notation for very large/small numbers
 */
export function formatNumberForDisplay(value: number): string {
  const normalized = normalizeNumber(value);

  // Use scientific notation for very large or very small numbers
  if (
    Math.abs(normalized) > 1e6 ||
    (Math.abs(normalized) < 1e-3 && normalized !== 0)
  ) {
    return normalized.toExponential(4);
  }

  // Use standard formatting for normal numbers
  return normalized.toString();
}
